import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Pencil, Trash2, Plus, Upload, Users, Save, X } from 'lucide-react';
import DataTable from '../components/DataTable';
import SearchBar from '../components/SearchBar';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import ConfirmDialog from '../components/ConfirmDialog';
import { useLanguage } from '../context/LanguageContext';

const EMPTY_FORM = {
  name_emp: '', last_name_emp: '', badge_num_emp: '', department_emp: '',
  company_emp: '', location_emp: '', type_emp: '', status_emp: 'Active',
  shift_start: '07:00', shift_end: '15:30'
};

export default function Employees() {
  const { t } = useLanguage();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [reason, setReason] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const limit = 50;

  // Excel upload state
  const fileRef = useRef();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadData, setUploadData] = useState([]);
  const [savingUpload, setSavingUpload] = useState(false);

  // Duplicate detection state
  const [dupModalOpen, setDupModalOpen] = useState(false);
  const [dupPairs, setDupPairs] = useState([]);
  const [dupSelections, setDupSelections] = useState({});
  const [mergingDup, setMergingDup] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/employees', { params: { search, page, limit } });
      setData(res.data.data);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: t('common.error'), text: t('emp.fetchError') || 'Error loading employees.' });
    }
    setLoading(false);
  }, [search, page, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setReason('');
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name_emp: row.name_emp || '',
      last_name_emp: row.last_name_emp || '',
      badge_num_emp: row.badge_num_emp || '',
      department_emp: row.department_emp || '',
      company_emp: row.company_emp || '',
      location_emp: row.location_emp || '',
      type_emp: row.type_emp || '',
      status_emp: row.status_emp || 'Active',
      shift_start: (row.shift_start || '07:00:00').substring(0, 5),
      shift_end: (row.shift_end || '15:30:00').substring(0, 5),
    });
    setReason('');
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (editing && !reason.trim()) {
      Swal.fire({ icon: 'warning', title: t('audit.reasonRequired') || 'Razon requerida', text: t('audit.reasonRequiredMsg') || 'Indica el motivo del cambio.' });
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`/api/employees/${editing.id_emp}`, { ...form, reason });
      } else {
        await axios.post('/api/employees', form);
      }
      setModalOpen(false);
      fetchData();
      Swal.fire({ icon: 'success', title: editing ? t('emp.updated') : t('emp.created'), timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('common.saveError') });
    }
    setSaving(false);
  };

  const askDelete = async (id) => {
    const { value: reasonInput } = await Swal.fire({
      title: t('emp.deleteTitle') || 'Eliminar empleado?',
      input: 'textarea',
      inputLabel: t('audit.reasonRequired') || 'Razon (obligatoria)',
      inputPlaceholder: t('audit.reasonPh') || 'Por que se esta eliminando?',
      inputValidator: (v) => !v?.trim() && (t('audit.reasonRequiredMsg') || 'La razon es obligatoria.'),
      showCancelButton: true,
      confirmButtonText: t('common.delete') || 'Eliminar',
      cancelButtonText: t('common.cancel') || 'Cancelar',
      confirmButtonColor: '#ef4444',
    });
    if (!reasonInput) return;
    setDeleteReason(reasonInput);
    setDeleteId(id);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/api/employees/${deleteId}`, { data: { reason: deleteReason } });
      setDeleteId(null);
      setDeleteReason('');
      fetchData();
      Swal.fire({ icon: 'success', title: t('emp.deleted'), timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('common.deleteError') });
    }
  };

  // Normalize any time format to HH:MM (24h)
  const normalizeTime = (val) => {
    if (val == null || val === '') return '';
    if (typeof val === 'number') {
      // Whole number 0-23 → treat as hour (e.g. 1 = 01:00, 13 = 13:00)
      if (Number.isInteger(val) && val >= 0 && val <= 23) {
        return `${String(val).padStart(2, '0')}:00`;
      }
      // Excel serial time (0.0 - 1.0), e.g. 0.3125 = 07:30
      if (val > 0 && val < 1) {
        const totalMin = Math.round(val * 24 * 60);
        const h = Math.floor(totalMin / 60) % 24;
        const m = totalMin % 60;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      }
      // Decimal like 7.5 = 07:30, 13.5 = 13:30
      if (val > 0 && val < 24) {
        const h = Math.floor(val);
        const m = Math.round((val - h) * 60);
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      }
      return '';
    }
    let s = String(val).trim();
    // Plain number as string: "1", "13", "7.5"
    const numVal = parseFloat(s);
    if (!isNaN(numVal) && /^\d+\.?\d*$/.test(s) && numVal >= 0 && numVal < 24) {
      if (Number.isInteger(numVal)) return `${String(numVal).padStart(2, '0')}:00`;
      const h = Math.floor(numVal);
      const m = Math.round((numVal - h) * 60);
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
    // 12h format: "7:00 AM", "3:30 PM", "07:00AM", "3:30pm", "1:00 PM", etc.
    const match12 = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM|am|pm|a\.m\.|p\.m\.)$/i);
    if (match12) {
      let h = parseInt(match12[1], 10);
      const m = parseInt(match12[2], 10);
      const period = match12[3].replace(/\./g, '').toUpperCase();
      if (period === 'PM' && h !== 12) h += 12;
      if (period === 'AM' && h === 12) h = 0;
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }
    // HH:MM or HH:MM:SS (24h) — just take HH:MM
    const match24 = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (match24) {
      let h = parseInt(match24[1], 10);
      const m = parseInt(match24[2], 10);
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      }
    }
    // Fallback
    return s.substring(0, 5);
  };

  // Excel upload handlers
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const XLSX = await import('xlsx');
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet);

      if (json.length === 0) {
        Swal.fire({ icon: 'warning', title: t('common.emptyFile'), text: t('common.emptyFileMsg') });
        e.target.value = '';
        return;
      }

      const employees = json.map(row => ({
        badge: Number(row['Badge'] || row['badge'] || row['badge_num_emp'] || 0),
        firstName: row['Nombre'] || row['nombre'] || row['name_emp'] || '',
        lastName: row['Apellido'] || row['apellido'] || row['last_name_emp'] || '',
        department_emp: row['Departamento'] || row['departamento'] || row['department_emp'] || '',
        company_emp: row['Compania'] || row['compania'] || row['company_emp'] || '',
        // En mayusculas y solo si es una ubicacion conocida: el campo ahora es un select, y "mia" o
        // "TMS MIA" no casarian con ninguna opcion. Lo que no se reconozca queda vacio y en rojo.
        facility: (() => {
          const f = String(row['Ubicacion'] || row['ubicacion'] || row['location_emp'] || '').trim().toUpperCase();
          return ['MIA', 'AUSWH', 'MX'].includes(f) ? f : '';
        })(),
        type_emp: (row['Tipo'] || row['tipo'] || row['type_emp'] || '').toUpperCase(),
        shift_start: normalizeTime(row['Entrada'] || row['entrada'] || row['shift_start']) || '07:00',
        shift_end: normalizeTime(row['Salida'] || row['salida'] || row['shift_end']) || '15:30',
      }));

      setUploadData(employees);
      setUploadModalOpen(true);
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: t('common.fileReadError') });
    }
    e.target.value = '';
  };

  const updateUploadRow = (index, field, value) => {
    setUploadData(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSaveUpload = async () => {
    const incomplete = uploadData.filter(e => !e.badge || !e.firstName || !e.type_emp || !e.facility);
    if (incomplete.length > 0) {
      Swal.fire({ icon: 'warning', title: t('emp.incomplete'), text: t('emp.incompleteMsg') });
      return;
    }
    setSavingUpload(true);
    try {
      const res = await axios.post('/api/employees/bulk-create', { employees: uploadData });
      setUploadModalOpen(false);
      setUploadData([]);
      fetchData();
      Swal.fire({ icon: 'success', title: t('emp.imported'), text: `${res.data.created} ${t('emp.importedMsg')}`, timer: 2000, showConfirmButton: false });
    } catch (err) {
      if (err.response?.data?.error === 'DUPLICATE_BADGE') {
        const badge = err.response.data.badge || '';
        Swal.fire({ icon: 'warning', title: t('emp.dupBadgeTitle'), text: t('emp.dupBadgeMsg').replace('{{badge}}', badge) });
      } else {
        Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('emp.importError') });
      }
    }
    setSavingUpload(false);
  };

  // Duplicate detection handlers
  const handleDetectDuplicates = async () => {
    try {
      Swal.fire({ title: t('emp.searchingDups'), allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      const res = await axios.get('/api/employees/duplicates');
      Swal.close();
      if (res.data.length === 0) {
        Swal.fire({ icon: 'success', title: t('emp.noDups'), text: t('emp.noDupsMsg'), timer: 2000, showConfirmButton: false });
        return;
      }
      setDupPairs(res.data);
      const initial = {};
      res.data.forEach((pair, i) => {
        initial[i] = { keepId: pair.keep.id_emp, nameFromId: pair.keep.id_emp };
      });
      setDupSelections(initial);
      setDupModalOpen(true);
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: t('emp.mergeError') });
    }
  };

  const updateDupSelection = (index, field, value) => {
    setDupSelections(prev => ({ ...prev, [index]: { ...prev[index], [field]: value } }));
  };

  const handleMergePair = async (pair, index) => {
    const sel = dupSelections[index] || { keepId: pair.keep.id_emp, nameFromId: pair.keep.id_emp };
    const keepEmp = sel.keepId === pair.keep.id_emp ? pair.keep : pair.dup;
    const removeEmp = sel.keepId === pair.keep.id_emp ? pair.dup : pair.keep;
    const nameEmp = sel.nameFromId === pair.keep.id_emp ? pair.keep : pair.dup;

    const confirmResult = await Swal.fire({
      icon: 'warning',
      title: t('emp.confirmMerge'),
      html: `<b>${t('emp.keepBadge')} ${keepEmp.badge_num_emp}</b><br>` +
        `<b>${t('emp.removeBadge')} ${removeEmp.badge_num_emp}</b> (${removeEmp.name_emp} ${removeEmp.last_name_emp})<br>` +
        `<b>${t('emp.finalName')}</b> ${nameEmp.name_emp} ${nameEmp.last_name_emp}<br><br>` +
        t('emp.mergeWarning'),
      showCancelButton: true,
      confirmButtonText: t('emp.merge'),
      cancelButtonText: t('common.cancel'),
      customClass: { confirmButton: 'btn-swal-danger' },
    });
    if (!confirmResult.isConfirmed) return;

    setMergingDup(pair.dup.id_emp + '_' + pair.keep.id_emp);
    try {
      const mergeData = { keepId: keepEmp.id_emp, removeId: removeEmp.id_emp };
      if (sel.nameFromId !== sel.keepId) {
        mergeData.updateName = { name_emp: nameEmp.name_emp, last_name_emp: nameEmp.last_name_emp };
      }
      const res = await axios.post('/api/employees/merge', mergeData);
      Swal.fire({
        icon: 'success',
        title: t('emp.merged'),
        html: `${res.data.message}<br>${t('emp.mergeResult').replace('{{timestamps}}', res.data.timestamps).replace('{{timecards}}', res.data.timecards).replace('{{absences}}', res.data.absences)}`,
        timer: 3000,
      });
      setDupPairs(prev => prev.filter((_, idx) => idx !== index));
      setDupSelections(prev => {
        const updated = {};
        Object.keys(prev).forEach(k => { if (Number(k) !== index) updated[k] = prev[k]; });
        return updated;
      });
      await fetchData();
    } catch (err) {
      Swal.fire({ icon: 'error', title: t('common.error'), text: err.response?.data?.error || t('emp.mergeError') });
    }
    setMergingDup(null);
  };


  const columns = [
    { header: t('common.badge'), accessor: 'badge_num_emp' },
    { header: t('common.name'), render: (r) => `${r.name_emp || ''} ${r.last_name_emp || ''}` },
    { header: t('common.department'), accessor: 'department_emp' },
    { header: t('common.company'), accessor: 'company_emp' },
    { header: t('common.location'), accessor: 'location_emp' },
    { header: t('common.type'), accessor: 'type_emp' },
    { header: t('emp.schedule'), render: (r) => {
      const s = (r.shift_start || '07:00:00').substring(0, 5);
      const e = (r.shift_end || '15:30:00').substring(0, 5);
      return <span style={{ fontSize: 12 }}>{s} - {e}</span>;
    }},
    { header: t('common.status'), render: (r) => { const sl = { 'Active': t('status.active'), 'Inactive': t('status.inactive'), 'Terminated': t('status.terminated') }; return <span className={`badge ${r.status_emp === 'Active' ? 'badge-success' : 'badge-info'}`}>{sl[r.status_emp] || r.status_emp || '-'}</span>; } },
    {
      header: t('common.actions'),
      style: { width: 120 },
      render: (r) => (
        <div className="table-actions">
          <button className="btn-secondary btn-sm" onClick={() => openEdit(r)}><Pencil size={14} style={{ marginRight: 4 }} />{t('common.edit')}</button>
          <button className="btn-danger btn-sm" onClick={() => askDelete(r.id_emp)}><Trash2 size={14} style={{ marginRight: 4 }} />{t('common.delete')}</button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>{t('emp.title')}</h1>
        <div className="page-header-actions">
          <SearchBar value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder={t('emp.search')} />
          <button className="btn-secondary" style={{ width: 'auto', padding: '8px 20px', whiteSpace: 'nowrap', borderColor: 'var(--warning)', color: 'var(--warning)' }} onClick={handleDetectDuplicates}>
            <Users size={15} style={{ marginRight: 6, flexShrink: 0 }} />{t('emp.detectDups')}
          </button>
<button className="btn-secondary" style={{ width: 'auto', padding: '8px 20px', whiteSpace: 'nowrap' }} onClick={() => fileRef.current?.click()}>
            <Upload size={15} style={{ marginRight: 6, flexShrink: 0 }} />{t('common.importExcel')}
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleFileUpload} />
          <button className="btn-primary" style={{ width: 'auto', padding: '8px 20px', whiteSpace: 'nowrap' }} onClick={openCreate}><Plus size={16} style={{ marginRight: 4, flexShrink: 0 }} />{t('common.new')}</button>
        </div>
      </div>

      <div className="table-container">
        <DataTable columns={columns} data={data} loading={loading} emptyMessage={t('emp.empty')} />
        <Pagination page={page} limit={limit} total={total} onPageChange={setPage} />
      </div>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? t('emp.editTitle') : t('emp.newTitle')} large>
        <form onSubmit={handleSave}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('emp.badgeNumber')} *</label>
                <input className="form-input" type="number" required value={form.badge_num_emp} onChange={e => setForm({ ...form, badge_num_emp: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('common.status')}</label>
                <select className="form-input" value={form.status_emp} onChange={e => setForm({ ...form, status_emp: e.target.value })}>
                  <option value="Active">{t('status.active')}</option>
                  <option value="Inactive">{t('status.inactive')}</option>
                  <option value="Terminated">{t('status.terminated')}</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('emp.nameReq')}</label>
                <input className="form-input" required value={form.name_emp} onChange={e => setForm({ ...form, name_emp: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('emp.lastNameReq')}</label>
                <input className="form-input" required value={form.last_name_emp} onChange={e => setForm({ ...form, last_name_emp: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('common.department')}</label>
                <input className="form-input" value={form.department_emp} onChange={e => setForm({ ...form, department_emp: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('common.company')}</label>
                <input className="form-input" value={form.company_emp} onChange={e => setForm({ ...form, company_emp: e.target.value })} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('common.location')} *</label>
                <select className="form-input" required value={form.location_emp} onChange={e => setForm({ ...form, location_emp: e.target.value })}>
                  <option value="">{t('common.select')}</option>
                  <option value="MIA">MIA</option>
                  <option value="AUSWH">AUSWH</option>
                  <option value="MX">MX</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t('common.type')} *</label>
                <select className="form-input" required value={form.type_emp} onChange={e => setForm({ ...form, type_emp: e.target.value })}>
                  <option value="">{t('common.select')}</option>
                  <option value="PERM">PERM</option>
                  <option value="PERM-HOUR">PERM-HOUR</option>
                  <option value="PERM-SALARY">PERM-SALARY</option>
                  <option value="TEMP">TEMP</option>
                  <option value="CONTR">CONTR</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('emp.shiftStart')}</label>
                <input className="form-input" type="time" value={form.shift_start} onChange={e => setForm({ ...form, shift_start: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('emp.shiftEnd')}</label>
                <input className="form-input" type="time" value={form.shift_end} onChange={e => setForm({ ...form, shift_end: e.target.value })} />
              </div>
            </div>
            {editing && (
              <div className="form-row">
                <div className="form-group" style={{ width: '100%' }}>
                  <label className="form-label">
                    {t('audit.reason') || 'Razon'} <span style={{ color: 'var(--danger)' }}>*</span>
                  </label>
                  <textarea
                    className="form-input"
                    rows={2}
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                    placeholder={t('audit.reasonPh') || 'Por que se esta editando?'}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}><X size={15} style={{ marginRight: 4 }} />{t('common.cancel')}</button>
            <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '8px 20px' }} disabled={saving}>
              <Save size={15} style={{ marginRight: 4 }} />{saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal: Import Excel Preview */}
      <Modal isOpen={uploadModalOpen} onClose={() => setUploadModalOpen(false)} title={`${t('emp.importTitle')} (${uploadData.length})`} xl>
        <div className="modal-body">
          <p style={{ color: 'var(--text-secondary)', marginBottom: 16, fontSize: 14 }}>
            {t('emp.importReview')}
          </p>
          <div style={{ maxHeight: '60vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {uploadData.map((emp, i) => (
              <div key={i} style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: 14, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10 }}>#{i + 1}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
                  <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {t('common.badge')} *
                    <input className="form-input" style={{ padding: '6px 10px', fontSize: 13, marginTop: 4 }}
                      type="number" value={emp.badge} onChange={e => updateUploadRow(i, 'badge', Number(e.target.value))} />
                  </label>
                  <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {t('common.name')} *
                    <input className="form-input" style={{ padding: '6px 10px', fontSize: 13, marginTop: 4 }}
                      value={emp.firstName} onChange={e => updateUploadRow(i, 'firstName', e.target.value)} />
                  </label>
                  <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {t('common.lastName')}
                    <input className="form-input" style={{ padding: '6px 10px', fontSize: 13, marginTop: 4 }}
                      value={emp.lastName} onChange={e => updateUploadRow(i, 'lastName', e.target.value)} />
                  </label>
                  <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {t('emp.typeReq')} *
                    <select className="form-input"
                      style={{ padding: '6px 10px', fontSize: 13, marginTop: 4, borderColor: !emp.type_emp ? 'var(--danger)' : undefined }}
                      value={emp.type_emp} onChange={e => updateUploadRow(i, 'type_emp', e.target.value)}>
                      <option value="">...</option>
                      <option value="PERM">PERM</option>
                      <option value="PERM-HOUR">PERM-HOUR</option>
                      <option value="PERM-SALARY">PERM-SALARY</option>
                      <option value="TEMP">TEMP</option>
                      <option value="CONTR">CONTR</option>
                    </select>
                  </label>
                  <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {t('common.department')}
                    <select className="form-input" style={{ padding: '6px 10px', fontSize: 13, marginTop: 4 }}
                      value={emp.department_emp} onChange={e => updateUploadRow(i, 'department_emp', e.target.value)}>
                      <option value="">...</option>
                      <option value="TMS MIA">TMS MIA</option>
                      <option value="TMS AUSWH">TMS AUSWH</option>
                    </select>
                  </label>
                  <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {t('common.company')}
                    <input className="form-input" style={{ padding: '6px 10px', fontSize: 13, marginTop: 4 }}
                      value={emp.company_emp} onChange={e => updateUploadRow(i, 'company_emp', e.target.value)} />
                  </label>
                  <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {t('common.location')} *
                    <select className="form-input"
                      style={{ padding: '6px 10px', fontSize: 13, marginTop: 4, borderColor: !emp.facility ? 'var(--danger)' : undefined }}
                      value={emp.facility} onChange={e => updateUploadRow(i, 'facility', e.target.value)}>
                      <option value="">...</option>
                      <option value="MIA">MIA</option>
                      <option value="AUSWH">AUSWH</option>
                      <option value="MX">MX</option>
                    </select>
                  </label>
                  <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {t('emp.entry')}
                    <input className="form-input" type="time" style={{ padding: '6px 10px', fontSize: 13, marginTop: 4 }}
                      value={emp.shift_start} onChange={e => updateUploadRow(i, 'shift_start', e.target.value)} />
                  </label>
                  <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                    {t('emp.exit')}
                    <input className="form-input" type="time" style={{ padding: '6px 10px', fontSize: 13, marginTop: 4 }}
                      value={emp.shift_end} onChange={e => updateUploadRow(i, 'shift_end', e.target.value)} />
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={() => setUploadModalOpen(false)}><X size={15} style={{ marginRight: 4 }} />{t('common.cancel')}</button>
          <button type="button" className="btn-primary" style={{ width: 'auto', padding: '8px 24px' }}
            onClick={handleSaveUpload} disabled={savingUpload}>
            <Upload size={15} style={{ marginRight: 6 }} />{savingUpload ? t('common.saving') : `${t('rep.import')} ${uploadData.length}`}
          </button>
        </div>
      </Modal>

      {/* Modal: Duplicate Employees */}
      <Modal isOpen={dupModalOpen} onClose={() => setDupModalOpen(false)} title={`${t('emp.dupTitle')} (${dupPairs.length})`} xl>
        <div className="modal-body">
          <p style={{ color: 'var(--text-secondary)', marginBottom: 16, fontSize: 14 }}>
            {t('emp.dupDesc')}
          </p>
          {dupPairs.length === 0 && (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 20 }}>{t('emp.dupResolved')}</p>
          )}
          {dupPairs.map((pair, i) => {
            const sel = dupSelections[i] || { keepId: pair.keep.id_emp, nameFromId: pair.keep.id_emp };
            const pairKey = pair.dup.id_emp + '_' + pair.keep.id_emp;
            const isMerging = mergingDup === pairKey;
            const empA = pair.dup;
            const empB = pair.keep;

            return (
              <div key={i} style={{
                border: '1px solid var(--warning)',
                borderRadius: 'var(--radius)',
                padding: 16,
                marginBottom: 12,
                background: 'var(--bg-tertiary)',
              }}>
                {/* Badge selection */}
                <div style={{ marginBottom: 12 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>
                    {t('emp.badgeKeep')}
                  </span>
                  {[empA, empB].map(emp => (
                    <label key={emp.id_emp} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', marginBottom: 4,
                      borderRadius: 'var(--radius)', cursor: 'pointer',
                      border: sel.keepId === emp.id_emp ? '2px solid var(--success)' : '1px solid var(--border)',
                      background: sel.keepId === emp.id_emp ? 'rgba(34,197,94,0.08)' : 'transparent',
                    }}>
                      <input type="radio" name={`keep_${i}`} checked={sel.keepId === emp.id_emp}
                        onChange={() => updateDupSelection(i, 'keepId', emp.id_emp)} />
                      <span style={{ fontWeight: 700, minWidth: 80 }}>Badge {emp.badge_num_emp}</span>
                      <span>{emp.name_emp} {emp.last_name_emp}</span>
                      <span className="badge badge-info" style={{ fontSize: 11 }}>{emp.location_emp}</span>
                      <span className="badge badge-warning" style={{ fontSize: 11 }}>{emp.type_emp}</span>
                      {sel.keepId === emp.id_emp && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--success)', marginLeft: 'auto' }}>{t('emp.keep')}</span>}
                      {sel.keepId !== emp.id_emp && <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--danger)', marginLeft: 'auto' }}>{t('emp.remove')}</span>}
                    </label>
                  ))}
                </div>

                {/* Name selection */}
                <div style={{ marginBottom: 12 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 6, display: 'block' }}>
                    {t('emp.correctName')}
                  </span>
                  {[empA, empB].map(emp => {
                    const fullName = `${emp.name_emp} ${emp.last_name_emp}`.trim();
                    return (
                      <label key={emp.id_emp} style={{
                        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', marginBottom: 4,
                        borderRadius: 'var(--radius)', cursor: 'pointer',
                        border: sel.nameFromId === emp.id_emp ? '2px solid var(--primary)' : '1px solid var(--border)',
                        background: sel.nameFromId === emp.id_emp ? 'rgba(59,130,246,0.08)' : 'transparent',
                      }}>
                        <input type="radio" name={`name_${i}`} checked={sel.nameFromId === emp.id_emp}
                          onChange={() => updateDupSelection(i, 'nameFromId', emp.id_emp)} />
                        <span style={{ fontWeight: sel.nameFromId === emp.id_emp ? 700 : 400 }}>{fullName}</span>
                        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>(Badge {emp.badge_num_emp})</span>
                      </label>
                    );
                  })}
                </div>

                {/* Merge button */}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    className="btn-primary"
                    style={{ width: 'auto', padding: '6px 20px', fontSize: 13 }}
                    onClick={() => handleMergePair(pair, i)}
                    disabled={isMerging}
                  >
                    {isMerging ? t('emp.merging') : t('emp.merge')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={() => setDupModalOpen(false)}>{t('common.close')}</button>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        message={t('emp.deleteConfirm')}
      />
    </div>
  );
}
